import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, MessageSquare } from 'lucide-react'; // Added MessageSquare for WhatsApp
import Logo from '@/components/layout/Logo';
import { useQuery } from '@tanstack/react-query'; // Import useQuery
import { fetchSystemSettings } from '@/lib/api'; // Import fetch function

const Footer = () => {
  // Fetch system settings using React Query
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['system-settings'], // Use the same key as in SystemSettings page
    queryFn: fetchSystemSettings,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour, for example
  });

  // Helper to format WhatsApp URL
  const formatWhatsAppUrl = (phone: string | undefined | null): string => {
    if (!phone) return '#'; // Return '#' or some default if no phone number
    // Remove non-numeric characters, keep '+' if present at the start
    const numericPhone = phone.replace(/(?!^)[^0-9]/g, ''); 
    return `https://wa.me/${numericPhone}`;
  };

  // Extract social media links safely
  const socialLinks = (settings?.social_media_links && typeof settings.social_media_links === 'object') 
    ? settings.social_media_links as { instagram?: string; facebook?: string; tiktok?: string } 
    : {};

  return (
    <footer className="bg-navy-800 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8"> {/* Adjusted grid columns back to 3 */}
          
          {/* Store Info (Takes more space now) */}
          <div className="md:col-span-1"> {/* Changed span back to 1, or adjust as needed */}
            <Logo size="lg" withText={true} />
            <p className="text-gray-300 mt-4 mb-4 text-sm">
              Sua loja especializada em aquarismo, oferecendo uma ampla variedade de peixes, plantas, 
              aquários e acessórios de alta qualidade.
            </p>
            {/* Contact Info from Settings */}
            <div className="space-y-2">
              {settings?.contact_phone && (
                <div className="flex items-center">
                  <Phone size={16} className="mr-2 text-orange-400 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">{settings.contact_phone}</span>
                </div>
              )}
               {settings?.store_address && (
                 <div className="flex items-start">
                  <MapPin size={16} className="mr-2 mt-1 text-orange-400 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">{settings.store_address}</span>
                </div>
               )}
            </div>
            {/* Social Media Links */}
             <div className="flex space-x-4 mt-6">
                {socialLinks.instagram && (
                    <a href={socialLinks.instagram.startsWith('http') ? socialLinks.instagram : `https://instagram.com/${socialLinks.instagram.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-400 transition-colors">
                       {/* Instagram Icon */}
                       <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                    </a>
                )}
                 {socialLinks.facebook && (
                    <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-400 transition-colors">
                       {/* Facebook Icon */}
                       <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                    </a>
                )}
                 {socialLinks.tiktok && (
                    <a href={socialLinks.tiktok.startsWith('http') ? socialLinks.tiktok : `https://tiktok.com/${socialLinks.tiktok}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-400 transition-colors">
                       {/* TikTok Icon */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16.5 6.5a4.5 4.5 0 1 0-9 0 4.5 4.5 0 0 0 9 0zM7.5 15.5V6.5M16.5 6.5S14 8.5 12 8.5 7.5 6.5 7.5 6.5M12 8.5V17l4-1.5"></path></svg>                    
                    </a>
                )}
             </div>
          </div>

          {/* Quick Links (Without Categories) */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Links Rápidos</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="text-gray-300 hover:text-orange-400 transition-colors">Início</Link></li>
              <li><Link to="/catalog" className="text-gray-300 hover:text-orange-400 transition-colors">Produtos</Link></li>
              {/* Removed Category Links */}
              <li><Link to="/legal/terms-of-use" className="text-gray-300 hover:text-orange-400 transition-colors">Termos de Uso</Link></li>
              <li><Link to="/legal/privacy-policy" className="text-gray-300 hover:text-orange-400 transition-colors">Política de Privacidade</Link></li>
              <li><Link to="/legal/return-policy" className="text-gray-300 hover:text-orange-400 transition-colors">Política de Devolução</Link></li>
              <li><Link to="/legal/tax-info" className="text-gray-300 hover:text-orange-400 transition-colors">Informações Fiscais</Link></li>
            </ul>
          </div>

          {/* Hours */}
          <div>
             <h3 className="text-lg font-semibold mb-4">Horário</h3>
             {settings?.business_hours ? (
                 <p className="text-gray-300 text-sm whitespace-pre-line">{settings.business_hours}</p>
             ) : (
                <ul className="space-y-1 text-sm">
                  <li className="flex justify-between text-gray-300"><span>Segunda a Sexta:</span><span>9:00 - 18:00</span></li>
                  <li className="flex justify-between text-gray-300"><span>Sábado:</span><span>9:00 - 14:00</span></li>
                  <li className="flex justify-between text-gray-300"><span>Domingo:</span><span>Fechado</span></li>
                </ul>
             )}
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-navy-700 text-center text-gray-400 text-xs">
           <p>© {new Date().getFullYear()} {settings?.store_name || 'Sua Loja'}. Todos os direitos reservados.</p> 
           {settings?.website_url && <p className="mt-1"><a href={settings.website_url} target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors">{settings.website_url}</a></p>}
        </div>
      </div>

      {/* WhatsApp Button */}
      {settings?.order_whatsapp && (
         <a 
            href={formatWhatsAppUrl(settings.order_whatsapp)} 
            target="_blank" 
            rel="noopener noreferrer"
            className="fixed bottom-5 right-5 z-40 inline-flex items-center p-3 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            aria-label="Fale conosco pelo WhatsApp"
         >
            <MessageSquare size={24} />
         </a>
      )}
    </footer>
  );
};

export default Footer;
